import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import webservice_url from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import CryptoJS from "crypto-js";
import "./profile.css";
import dayjs from "dayjs";
import Footer from "../../../core/common/Footer";
const STORAGE_KEY = "auth";
const MIN_PASSWORD_LENGTH = 8;

type PasswordField = "currentPassword" | "newPassword" | "confirmPassword";

interface StoredUser {
  email?: string;
  full_name?: string;
  lastLogin?: string;
  profile_name?: string;
  orgId?: string;
  phone?: string;
  profileId?: number | string;
  token?: string;
  userId?: string;
}

const getStoredUser = (): StoredUser | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getInitials = (name?: string) => {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")
  ).toUpperCase();
};

const STRENGTH_META = [
  { label: "", color: "#e5e7eb" },
  { label: "Weak", color: "#ef4444" },
  { label: "Fair", color: "#f59e0b" },
  { label: "Good", color: "#3b82f6" },
  { label: "Strong", color: "#10b981" },
];

const Profile = () => {
  const [user, setUser] = useState<StoredUser | null>(null);

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState<Partial<Record<PasswordField, string>>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setAlert(null);
  };

  const validate = () => {
    const newErrors: Partial<Record<PasswordField, string>> = {};
    const { currentPassword, newPassword, confirmPassword } = passwords;

    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < MIN_PASSWORD_LENGTH) {
      newErrors.newPassword = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    } else if (newPassword === currentPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setErrors({});
    setAlert(null);
  };

  const ChangePasswordAPI = useApi<any>(webservice_url.change_password, {
    method: "PUT",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAlert(null);

    if (!validate()) return;

    setLoading(true);
    try {
      const hashedCurrentPassword = CryptoJS.SHA256(
        passwords.currentPassword,
      ).toString();
      const hashedNewPassword = CryptoJS.SHA256(
        passwords.newPassword,
      ).toString();
      const hashedConfirmPassword = CryptoJS.SHA256(
        passwords.confirmPassword,
      ).toString();

      const res: any = await ChangePasswordAPI.callApi({
        current_password: hashedCurrentPassword,
        new_password: hashedNewPassword,
        confirmPassword: hashedConfirmPassword,
      });

      if (res?.success === false || res?.status === false) {
        setAlert({
          type: "danger",
          message:
            res?.message || "Unable to change password. Please try again.",
        });
        return;
      }

      setAlert({
        type: "success",
        message: res?.message || "Password changed successfully.",
      });
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
    } catch (error: any) {
      setAlert({
        type: "danger",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------- UI helpers ----------
  const rules = useMemo(() => {
    const p = passwords.newPassword;
    return [
      {
        label: `At least ${MIN_PASSWORD_LENGTH} characters`,
        ok: p.length >= MIN_PASSWORD_LENGTH,
      },
      {
        label: "Upper and lower case letters",
        ok: /[A-Z]/.test(p) && /[a-z]/.test(p),
      },
      { label: "At least one number", ok: /[0-9]/.test(p) },
      { label: "At least one special character", ok: /[^A-Za-z0-9]/.test(p) },
    ];
  }, [passwords.newPassword]);

  const strength = passwords.newPassword ? rules.filter((r) => r.ok).length : 0;
  const strengthMeta = STRENGTH_META[strength];

  const renderPasswordField = (
    field: PasswordField,
    label: string,
    autoComplete: string,
    placeholder: string,
  ) => (
    <div className="pf-field">
      <label htmlFor={field}>{label}</label>
      <div className="pf-input-wrap">
        <input
          id={field}
          type={passwordVisibility[field] ? "text" : "password"}
          name={field}
          value={passwords[field]}
          onChange={handlePasswordChange}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className={`form-control ${errors[field] ? "is-invalid" : ""}`}
        />
        <button
          type="button"
          className="pf-eye"
          onClick={() => togglePasswordVisibility(field)}
          aria-label={
            passwordVisibility[field] ? "Hide password" : "Show password"
          }
        >
          <i
            className={`ti ${passwordVisibility[field] ? "ti-eye" : "ti-eye-off"}`}
          />
        </button>
      </div>
      {errors[field] && <div className="pf-error">{errors[field]}</div>}
    </div>
  );

  const renderInfoRow = (
    icon: string,
    label: string,
    value?: string | number,
  ) => (
    <div className="pf-info-row">
      <div className="pf-info-icon">
        <i className={`ti ${icon}`} />
      </div>
      <div>
        <div className="pf-info-label">{label}</div>
        <div className="pf-info-value">
          {value !== undefined && value !== "" ? String(value) : "-"}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Profile</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Profile
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Hero */}
          <div className="pf-hero">
            <div className="pf-hero-cover" />
            <div className="pf-hero-body">
              <div className="pf-avatar">{getInitials(user?.full_name)}</div>
              <div className="pf-hero-info">
                <h3>{user?.full_name || "User"}</h3>
                <div className="pf-hero-meta">
                  <span>
                    <i className="ti ti-mail" />
                    {user?.email || "-"}
                  </span>
                  <span>
                    <i className="ti ti-phone" />
                    {user?.phone || "-"}
                  </span>
                </div>
              </div>
              {/* <div className="pf-chip">
                <i className="ti ti-clock me-1" />
                Last login: {formatDateTime(user?.lastLogin)}
              </div> */}
              <div className="pf-chip">
                <i className="ti ti-clock me-1" />
                Last login:{" "}
                {user?.lastLogin
                  ? dayjs(user.lastLogin).format("DD MMM YYYY, hh:mm a")
                  : "N/A"}
              </div>
            </div>
          </div>
          {/* /Hero */}

          <div className="row g-3 pb-3">
            {/* Basic Information */}
            <div className="col-lg-5">
              <div className="pf-card">
                <div className="pf-card-head">
                  <div className="pf-card-icon">
                    <i className="ti ti-user" />
                  </div>
                  <div>
                    <h5>Basic information</h5>
                    <p>Your account details</p>
                  </div>
                </div>

                {renderInfoRow("ti-id-badge-2", "Full name", user?.full_name)}
                {renderInfoRow(
                  "ti-user-shield",
                  "Profile name",
                  user?.profile_name,
                )}
                {renderInfoRow("ti-mail", "Email", user?.email)}
                {renderInfoRow("ti-phone", "Phone", user?.phone)}
                {renderInfoRow(
                  "ti-clock",
                  "Last login",
                  formatDateTime(user?.lastLogin),
                )}
              </div>
            </div>

            {/* Change Password */}
            <div className="col-lg-7">
              <form onSubmit={handleSubmit} noValidate className="h-100">
                <div className="pf-card">
                  <div className="pf-card-head">
                    <div className="pf-card-icon">
                      <i className="ti ti-lock" />
                    </div>
                    <div>
                      <h5>Change password</h5>
                      <p>Use a strong password you don't use elsewhere</p>
                    </div>
                  </div>

                  {alert && (
                    <div className={`pf-alert ${alert.type}`} role="alert">
                      <i
                        className={`ti ${
                          alert.type === "success"
                            ? "ti-circle-check"
                            : "ti-alert-circle"
                        }`}
                      />
                      <span>{alert.message}</span>
                    </div>
                  )}

                  {renderPasswordField(
                    "currentPassword",
                    "Current password",
                    "current-password",
                    "Enter current password",
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      {renderPasswordField(
                        "newPassword",
                        "New password",
                        "new-password",
                        "Enter new password",
                      )}
                    </div>
                    <div className="col-md-6">
                      {renderPasswordField(
                        "confirmPassword",
                        "Confirm password",
                        "new-password",
                        "Re-enter new password",
                      )}
                    </div>
                  </div>

                  {passwords.newPassword && (
                    <div className="mb-3">
                      <div className="pf-meter" aria-hidden="true">
                        {[1, 2, 3, 4].map((i) => (
                          <span
                            key={i}
                            style={{
                              background:
                                i <= strength ? strengthMeta.color : undefined,
                            }}
                          />
                        ))}
                      </div>
                      <div className="pf-meter-label">
                        Password strength:{" "}
                        <strong style={{ color: strengthMeta.color }}>
                          {strengthMeta.label}
                        </strong>
                      </div>
                    </div>
                  )}

                  <ul className="pf-rules">
                    {rules.map((r) => (
                      <li key={r.label} className={r.ok ? "ok" : ""}>
                        <i
                          className={`ti ${r.ok ? "ti-circle-check" : "ti-circle"}`}
                        />
                        {r.label}
                      </li>
                    ))}
                  </ul>

                  <div className="pf-actions">
                    <button
                      type="button"
                      className="btn btn-outline-light border"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Updating...
                        </>
                      ) : (
                        "Update password"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default Profile;

// import React, { useState } from "react";
// import { Link } from "react-router-dom";
// import { all_routes } from "../../router/all_routes";
// import CommonSelect from "../../../core/common/commonSelect";
// import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
// type PasswordField =
//   | "oldPassword"
//   | "newPassword"
//   | "confirmPassword"
//   | "currentPassword";

// const Profile = () => {
//   const route = all_routes;
//   const [passwordVisibility, setPasswordVisibility] = useState({
//     oldPassword: false,
//     newPassword: false,
//     confirmPassword: false,
//     currentPassword: false,
//   });

//   const togglePasswordVisibility = (field: PasswordField) => {
//     setPasswordVisibility((prevState) => ({
//       ...prevState,
//       [field]: !prevState[field],
//     }));
//   };

//   const countryChoose = [
//     { value: "Select", label: "Select" },
//     { value: "USA", label: "USA" },
//     { value: "Canada", label: "Canada" },
//     { value: "Germany", label: "Germany" },
//     { value: "France", label: "France" },
//   ];
//   const stateChoose = [
//     { value: "Select", label: "Select" },
//     { value: "california", label: "california" },
//     { value: "Texas", label: "Texas" },
//     { value: "New York", label: "New York" },
//     { value: "Florida", label: "Florida" },
//   ];
//   const cityChoose = [
//     { value: "Select", label: "Select" },
//     { value: "Los Angeles", label: "Los Angeles" },
//     { value: "San Francisco", label: "San Francisco" },
//     { value: "San Diego", label: "San Diego" },
//     { value: "Fresno", label: "Fresno" },
//   ];
//   return (
//     <>
//       {/* Page Wrapper */}
//       <div className="page-wrapper">
//         <div className="content">
//           {/* Breadcrumb */}
//           <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//             <div className="my-auto mb-2">
//               <h2 className="mb-1">Profile </h2>
//               <nav>
//                 <ol className="breadcrumb mb-0">
//                   <li className="breadcrumb-item">
//                     <Link to="/">
//                       <i className="ti ti-smart-home" />
//                     </Link>
//                   </li>
//                   <li className="breadcrumb-item">Pages</li>
//                   <li className="breadcrumb-item active" aria-current="page">
//                     Profile{" "}
//                   </li>
//                 </ol>
//               </nav>
//             </div>
//             <div className="head-icons ms-2">
//               <CollapseHeader />
//             </div>
//           </div>
//           {/* /Breadcrumb */}
//           <div className="card">
//             <div className="card-body">
//               <div className="border-bottom mb-3 pb-3">
//                 <h4>Profile </h4>
//               </div>
//               <form action="profile.html">
//                 <div className="border-bottom mb-3">
//                   <div className="row">
//                     <div className="col-md-12">
//                       <div>
//                         <h6 className="mb-3">Basic Information</h6>
//                         <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
//                           <div className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames">
//                             <i className="ti ti-photo text-gray-3 fs-16" />
//                           </div>
//                           <div className="profile-upload">
//                             <div className="mb-2">
//                               <h6 className="mb-1">Profile Photo</h6>
//                               <p className="fs-12">
//                                 Recommended image size is 40px x 40px
//                               </p>
//                             </div>
//                             <div className="profile-uploader d-flex align-items-center">
//                               <div className="drag-upload-btn btn btn-sm btn-primary me-2">
//                                 Upload
//                                 <input
//                                   type="file"
//                                   className="form-control image-sign"
//                                   multiple
//                                 />
//                               </div>
//                               <Link to="#" className="btn btn-light btn-sm">
//                                 Cancel
//                               </Link>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">
//                             First Name
//                           </label>
//                         </div>
//                         <div className="col-md-8">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">
//                             Last Name
//                           </label>
//                         </div>
//                         <div className="col-md-8">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">Email</label>
//                         </div>
//                         <div className="col-md-8">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">Phone</label>
//                         </div>
//                         <div className="col-md-8">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="border-bottom mb-3">
//                   <h6 className="mb-3">Address Information</h6>
//                   <div className="row">
//                     <div className="col-md-12">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-2">
//                           <label className="form-label mb-md-0">Address</label>
//                         </div>
//                         <div className="col-md-10">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">Country</label>
//                         </div>
//                         <div className="col-md-8">
//                           <CommonSelect
//                             className="select"
//                             options={countryChoose}
//                             defaultValue={countryChoose[0]}
//                           />
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">State</label>
//                         </div>
//                         <div className="col-md-8">
//                           <div>
//                             <CommonSelect
//                               className="select"
//                               options={stateChoose}
//                               defaultValue={stateChoose[0]}
//                             />
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">City</label>
//                         </div>
//                         <div className="col-md-8">
//                           <div>
//                             <CommonSelect
//                               className="select"
//                               options={cityChoose}
//                               defaultValue={cityChoose[0]}
//                             />
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-6">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-4">
//                           <label className="form-label mb-md-0">
//                             Postal Code
//                           </label>
//                         </div>
//                         <div className="col-md-8">
//                           <input type="text" className="form-control" />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="border-bottom mb-3">
//                   <h6 className="mb-3">Change Password</h6>
//                   <div className="row">
//                     <div className="col-md-4">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-5">
//                           <label className="form-label mb-md-0">
//                             Current Password
//                           </label>
//                         </div>
//                         <div className="col-md-7">
//                           <div className="pass-group">
//                             <input
//                               type={
//                                 passwordVisibility.currentPassword
//                                   ? "text"
//                                   : "password"
//                               }
//                               className="pass-input form-control"
//                             />
//                             <span
//                               className={`ti toggle-passwords ${passwordVisibility.currentPassword
//                                   ? "ti-eye"
//                                   : "ti-eye-off"
//                                 }`}
//                               onClick={() =>
//                                 togglePasswordVisibility("currentPassword")
//                               }
//                             ></span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-4">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-5">
//                           <label className="form-label mb-md-0">
//                             New Password
//                           </label>
//                         </div>
//                         <div className="col-md-7">
//                           <div className="pass-group">
//                             <input
//                               type={
//                                 passwordVisibility.newPassword
//                                   ? "text"
//                                   : "password"
//                               }
//                               className="pass-input form-control"
//                             />
//                             <span
//                               className={`ti toggle-passwords ${passwordVisibility.newPassword
//                                   ? "ti-eye"
//                                   : "ti-eye-off"
//                                 }`}
//                               onClick={() =>
//                                 togglePasswordVisibility("newPassword")
//                               }
//                             ></span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="col-md-4">
//                       <div className="row align-items-center mb-3">
//                         <div className="col-md-5">
//                           <label className="form-label mb-md-0">
//                             Confirm Password
//                           </label>
//                         </div>
//                         <div className="col-md-7">
//                           <div className="pass-group">
//                             <input
//                               type={
//                                 passwordVisibility.confirmPassword
//                                   ? "text"
//                                   : "password"
//                               }
//                               className="pass-input form-control"
//                             />
//                             <span
//                               className={`ti toggle-passwords ${passwordVisibility.confirmPassword
//                                   ? "ti-eye"
//                                   : "ti-eye-off"
//                                 }`}
//                               onClick={() =>
//                                 togglePasswordVisibility("confirmPassword")
//                               }
//                             ></span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="d-flex align-items-center justify-content-end">
//                   <button
//                     type="button"
//                     className="btn btn-outline-light border me-3"
//                   >
//                     Cancel
//                   </button>
//                   <button type="submit" className="btn btn-primary">
//                     Save
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//         <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
//           <p className="mb-0">© 2026 Aupertz. All rights reserved.</p>
//           <p>
//             Designed &amp; Developed By{" "}
//             <Link to="#" className="text-primary">
//               Bizclockarts
//             </Link>
//           </p>
//         </div>
//       </div>
//       {/* /Page Wrapper */}
//     </>
//   );
// };

// export default Profile;
